import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download, RefreshCw, Sparkles, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import {
  getSurveyReport, getNarrativeJob, regenerateNarrative, publishNarrative, unpublishNarrative,
} from '../../../services/surveyReportService';
import { REPORT_POLL_MS, REPORT_MAX_POLLS, AI_JOB_POLL_MS } from './reportModel';
import ReportHeader from './sections/ReportHeader';
import OrgSummary from './sections/OrgSummary';
import DimensionResults from './sections/DimensionResults';
import QuestionResults from './sections/QuestionResults';
import BreakdownHeatmap from './sections/BreakdownHeatmap';
import CommentThemes from './sections/CommentThemes';
import Recommendations from './sections/Recommendations';
import Roadmap from './sections/Roadmap';
import MethodAndNextSteps from './sections/MethodAndNextSteps';
import AiPlusCard from './sections/AiPlusCard';
import ReportSettingsDrawer from './structure/ReportSettingsDrawer';
import NarrativeSectionDrawer from './narrative/NarrativeSectionDrawer';

// Anket sonuç raporu sekmesi (spec 2026-10-15 §10.1; 2026-10-16 S5/E1/E2). TEK kaydırma kabı: detay sayfasının TabContent'i
// overflow:hidden. "Anket Yapısı" görünümü YOK (yapı yayında kendiliğinden kurulur); yazar araç çubuğundan Rapor Ayarları
// çekmecesini açar. Sayısal rapor yalnız SV ister; AI+ yoksa (report.aiEnabled=false) AI bölümlerinin yerinde
// "AI+ ile açılır" kartları çizilir, AI düğmeleri görünmez.

const Scroll = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 24px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $published, theme }) => ($published ? theme.colors.report.strongBg : theme.colors.report.noteBg)};
  color: ${({ $published, theme }) => ($published ? theme.colors.report.blue : theme.colors.report.noteTitle)};
`;

const StateCard = styled.div`
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 8px;
  padding: 28px 32px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  h3 { margin: 0 0 6px; font-size: 16px; color: ${({ theme }) => theme.colors.text.primary}; }
  p { margin: 0 0 12px; font-size: 13px; color: ${({ theme }) => theme.colors.text.muted}; }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 8px;
`;

const Stat = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.report.brand};
  padding-top: 8px;
  span { display: block; font-size: 12px; color: ${({ theme }) => theme.colors.text.muted}; }
  strong { display: block; margin-top: 4px; font-size: 20px; font-weight: 800; color: ${({ theme }) => theme.colors.text.primary}; }
`;

const Banner = styled.div`
  padding: 10px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.report.noteBg};
  border: 1px solid ${({ theme }) => theme.colors.report.noteBorder};
  color: ${({ theme }) => theme.colors.report.noteTitle};
`;

const PublishHint = styled.span`
  max-width: 360px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export default function SurveyReportTab({ survey }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { canWrite } = useAuth();

  const [report, setReport] = useState(null);
  // loading | awaitingClose | computing | timeout | error | insufficient | ready
  const [phase, setPhase] = useState('loading');
  const [editSection, setEditSection] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const pollsRef = useRef(0);
  const timerRef = useRef(null);
  const jobTimerRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => () => {
    aliveRef.current = false;
    clearTimeout(timerRef.current);
    clearTimeout(jobTimerRef.current);
  }, []);

  const load = useCallback(async () => {
    clearTimeout(timerRef.current);
    const res = await getSurveyReport(survey.id);
    if (!aliveRef.current) return;
    const data = res?.data?.data;
    if (!res?.data?.status || !data) {
      setPhase('error');
      return;
    }
    setReport(data);
    // Anket sürerken yalnız katılım (sonuçlar kapanınca açılır — kullanıcı kararı 2026-10-16). Yoklama YOK.
    if (data.state === 'awaitingClose') {
      pollsRef.current = 0;
      setPhase('awaitingClose');
      return;
    }
    if (data.state === 'computing') {
      if (pollsRef.current >= REPORT_MAX_POLLS) {
        setPhase('timeout');
        return;
      }
      pollsRef.current += 1;
      setPhase('computing');
      timerRef.current = setTimeout(load, REPORT_POLL_MS);
      return;
    }
    pollsRef.current = 0;
    setPhase(data.state === 'insufficient' ? 'insufficient' : 'ready');
  }, [survey.id]);

  const reload = useCallback(() => {
    pollsRef.current = 0;
    setPhase('loading');
    load();
  }, [load]);

  // Durum değişince (ör. bu sayfada kapatıldı) rapor yeniden okunur — kapanınca "computing" yoklaması başlar. Sayısal rapor
  // yalnız SV ister (E1); AI+ kapısı yok — AI bölümleri report.aiEnabled ile.
  useEffect(() => {
    if (survey?.id) reload();
  }, [survey?.id, survey?.status, reload]);

  const job = report?.job || null;
  const jobActive = !!job && (job.status === 'queued' || job.status === 'running');

  // AI işi sürerken yoklama — iş bitince (done/failed) rapor yeniden okunur.
  useEffect(() => {
    if (!jobActive) return undefined;
    jobTimerRef.current = setTimeout(async () => {
      const res = await getNarrativeJob(survey.id);
      if (!aliveRef.current) return;
      const next = res?.data?.data;
      if (next && (next.status === 'done' || next.status === 'failed')) load();
      else setReport((prev) => (prev ? { ...prev, job: next || prev.job } : prev));
    }, AI_JOB_POLL_MS);
    return () => clearTimeout(jobTimerRef.current);
  }, [jobActive, report, survey.id, load]);

  const aiEnabled = !!report?.aiEnabled;
  const writer = canWrite('SV') && !!report?.canWrite;
  const aiWriter = writer && aiEnabled;
  const narrative = aiEnabled ? report?.narrative || null : null;
  const data = report?.snapshot || null;
  const hasComments = (data?.freeText || []).length > 0;
  // Kırılım bayrakları yayında kurulur (SurveyAutoStructure) — taslakta çekmece açılmaz (netleşen 13).
  const canOpenSettings = writer && survey.status !== 'draft';

  const runAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch {
      // interceptor toast'u basar
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };
  const generate = () => runAction(() => regenerateNarrative(survey.id, lang));
  const handleConfirmRegenerate = async () => {
    setConfirmOpen(false);
    await generate();
  };

  const handlePdf = async () => {
    setPdfBusy(true);
    setPdfFailed(false);
    try {
      const { downloadReportPdf } = await import('./pdf/downloadReportPdf');
      await downloadReportPdf({ report, t, lang, fileTitle: survey.title });
    } catch {
      if (aliveRef.current) setPdfFailed(true);   // tarayıcıda üretim — sunucu yok, interceptor da yok
    } finally {
      if (aliveRef.current) setPdfBusy(false);
    }
  };

  const renderState = () => {
    if (phase === 'awaitingClose') {
      const p = report?.participation || {};
      return (
        <StateCard data-testid="report-awaiting-close">
          <h3>{t('survey.report.awaitingClose')}</h3>
          <p>{t('survey.report.awaitingCloseHint')}</p>
          <Stats>
            {p.isPublic ? (
              <Stat>
                <span>{t('survey.responseCount')}</span>
                <strong>{p.responseCount ?? 0}</strong>
              </Stat>
            ) : (
              <>
                <Stat>
                  <span>{t('survey.participantCount')}</span>
                  <strong>{p.participantCount ?? 0}</strong>
                </Stat>
                <Stat>
                  <span>{t('survey.completedCount')}</span>
                  <strong>{p.completedCount ?? 0}</strong>
                </Stat>
                <Stat>
                  <span>{t('survey.completionRate')}</span>
                  <strong>{`${p.completionRate ?? 0}%`}</strong>
                </Stat>
              </>
            )}
          </Stats>
        </StateCard>
      );
    }
    if (phase === 'loading' || phase === 'computing') {
      return (
        <StateCard>
          <h3>{t('survey.report.computing')}</h3>
          <p>{t('survey.report.computingHint')}</p>
        </StateCard>
      );
    }
    if (phase === 'timeout' || phase === 'error') {
      return (
        <StateCard data-testid={`report-${phase}`}>
          {/* Final inceleme M9: 403/404/500 "henüz hazır değil" demesin — yalnız yoklama zaman aşımı öyle der. */}
          <p>{phase === 'timeout' ? t('survey.report.computingTimeout') : t('common.error')}</p>
          <Button variant="outline" onClick={reload}>{t('survey.report.retry')}</Button>
        </StateCard>
      );
    }
    if (phase === 'insufficient') {
      return (
        <StateCard data-testid="report-insufficient">
          <h3>{t('survey.report.insufficient', { n: data?.threshold ?? survey.anonymityThreshold })}</h3>
          <p>{t('survey.report.insufficientHint', { count: data?.participation?.responded ?? 0 })}</p>
        </StateCard>
      );
    }
    return null;
  };

  const renderAiCard = () => {
    if (!aiWriter) return null;
    if (jobActive) {
      return (
        <StateCard>
          <h3>{t('survey.report.aiRunning')}</h3>
          <p>{t('survey.report.aiRunningHint')}</p>
        </StateCard>
      );
    }
    if (!narrative && job?.status === 'failed') {
      return (
        <StateCard>
          <h3>{t('survey.report.aiFailed')}</h3>
          <p>{t(`responseErrors.${job.errorCode || 'survey.report.aiUnavailable'}`)}</p>
          <Button variant="outline" loading={busy} onClick={generate}>{t('survey.report.retry')}</Button>
        </StateCard>
      );
    }
    if (!narrative) {
      return (
        <StateCard>
          <h3>{t('survey.report.aiNotStarted')}</h3>
          <p>{t('survey.report.aiNotStartedHint')}</p>
          <Button loading={busy} onClick={generate}><Sparkles size={14} /> {t('survey.report.generate')}</Button>
        </StateCard>
      );
    }
    return null;
  };

  const sectionProps = { data, narrative, t, lang, canEdit: aiWriter && !!narrative && !jobActive, onEdit: setEditSection };

  return (
    <Scroll>
      {report && (
        <Toolbar>
          <Actions>
            {aiWriter && report.narrativeStatus && (
              <Badge $published={report.narrativeStatus === 'published'}>
                {report.narrativeStatus === 'published' ? t('survey.report.statusPublished') : t('survey.report.statusDraft')}
              </Badge>
            )}
          </Actions>
          <Actions>
            {canOpenSettings && (
              <Button variant="outline" onClick={() => setSettingsOpen(true)}>
                <SlidersHorizontal size={14} /> {t('survey.report.settings.title')}
              </Button>
            )}
            {phase === 'ready' && (
              <>
                {aiWriter && narrative && !jobActive && (
                  <Button variant="ghost" disabled={busy} onClick={() => setConfirmOpen(true)}>
                    <RefreshCw size={14} /> {t('survey.report.regenerate')}
                  </Button>
                )}
                {aiWriter && narrative && <PublishHint>{t('survey.report.publishHint')}</PublishHint>}
                {aiWriter && narrative && report.narrativeStatus === 'draft' && (
                  <Button variant="secondary" loading={busy} onClick={() => runAction(() => publishNarrative(survey.id))}>
                    {t('survey.report.publish')}
                  </Button>
                )}
                {aiWriter && narrative && report.narrativeStatus === 'published' && (
                  <Button variant="outline" loading={busy} onClick={() => runAction(() => unpublishNarrative(survey.id))}>
                    {t('survey.report.unpublish')}
                  </Button>
                )}
                <Button variant="primary" loading={pdfBusy} onClick={handlePdf}>
                  <Download size={14} /> {pdfBusy ? t('survey.report.pdfPreparing') : t('survey.report.downloadPdf')}
                </Button>
              </>
            )}
          </Actions>
        </Toolbar>
      )}

      {renderState()}
      {phase === 'ready' && data && (
        <>
          {pdfFailed && <Banner>{t('survey.report.pdfFailed')}</Banner>}
          {aiWriter && report.narrativeStale && <Banner>{t('survey.report.staleBanner')}</Banner>}
          {renderAiCard()}
          <ReportHeader data={data} organization={report.organization} t={t} lang={lang} />
          <OrgSummary {...sectionProps} />
          {!aiEnabled && <AiPlusCard area="assessment" t={t} />}
          <DimensionResults data={data} t={t} lang={lang} />
          <QuestionResults data={data} t={t} lang={lang} />
          <BreakdownHeatmap {...sectionProps} />
          <CommentThemes {...sectionProps} />
          {!aiEnabled && hasComments && <AiPlusCard area="themes" t={t} />}
          {aiEnabled ? (
            <>
              <Recommendations {...sectionProps} />
              <Roadmap {...sectionProps} />
            </>
          ) : (
            <AiPlusCard area="recommendations" t={t} />
          )}
          <MethodAndNextSteps {...sectionProps} />
        </>
      )}

      <ReportSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        surveyId={survey.id}
        settings={report?.settings}
        isPublic={!!survey.isPublic}
        questions={survey.questions || []}
        onSaved={reload}
      />
      <NarrativeSectionDrawer
        open={!!editSection}
        section={editSection}
        report={report}
        surveyId={survey.id}
        t={t}
        onClose={() => setEditSection(null)}
        onSaved={load}
      />
      <ConfirmDialog
        isOpen={confirmOpen}
        title={t('survey.report.regenerateConfirmTitle')}
        message={t('survey.report.regenerateConfirmBody')}
        confirmText={t('survey.report.regenerate')}
        cancelText={t('common.cancel')}
        type="danger"
        onConfirm={handleConfirmRegenerate}
        onCancel={() => setConfirmOpen(false)}
      />
    </Scroll>
  );
}
